import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaSave, FaTimes, FaEdit } from "react-icons/fa";
import topicService from "../../services/topicService";
import { values } from "lodash";

const visibilityLevels = [
  { value: "on-demand", label: "On Demand" },
  { value: "champions", label: "Champions" },
  { value: "presentation", label: "Presentation" },
];

const supportedModalityLevels = [
  { value: "text", label: "Text" },
  { value: "voice", label: "Voice" },
];

const ReleaseNoteTab = ({ promptKey, promptType }) => {
  const [editingVersion, setEditingId] = useState(null);
  const [editedNote, setEditedNote] = useState({});
  const [notes, setNotes] = useState({});
  const textareaRefs = useRef({});

  const fetchData = useCallback(async () => {
    const data = await topicService.getReleaseNotesByTopicKey(promptKey);
    setNotes(data ?? {});
  }, [promptKey]);

  useEffect(() => {
    fetchData();
    window.addEventListener("refreshReleaseNotes", fetchData);
    return () => {
      window.removeEventListener("refreshReleaseNotes", fetchData);
    };
  }, [fetchData]);

  useEffect(() => {
    Object.keys(notes).forEach((version) => {
      adjustTextareaHeight(textareaRefs.current[version]);
    });
  }, [notes]);

  const handleEdit = useCallback(
    (version) => {
      setEditingId(version);
      setEditedNote(notes.release_notes[version] || {});
    },
    [notes]
  );

  const handleSave = useCallback(
    async (version) => {
      try {
        notes.release_notes[version] = {
          ...notes.release_notes[version],
          ...editedNote,
        };
        let topicData = await topicService.getTopicByVersion(
          promptKey,
          version
        );
        topicData.version_comment = editedNote.comment || "";
        topicData.visibility = editedNote.visibility || [];
        topicData.supported_modalities = editedNote.supported_modalities || [
          "voice",
          "text",
        ];
        await topicService.updateTopicVersion(topicData);

        setNotes((prevNotes) => ({
          ...prevNotes,
          [version]: { ...prevNotes.release_notes[version], ...editedNote },
        }));

        setEditingId(null);
        setEditedNote({});
        await fetchData(); // Refresh the data after successful save
      } catch (error) {
        console.error("Error saving release note:", error);
        // You might want to show an error message to the user here
      }
    },
    [editedNote, promptKey, fetchData]
  );

  const handleCancel = useCallback(() => {
    setEditingId(null);
    setEditedNote({});
  }, []);

  const handleChange = useCallback((e) => {
    setEditedNote((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleCheckboxChange = useCallback((category, level, currentValues) => {
    setEditedNote((prev) => {
      return {
        ...prev,
        [category]: currentValues.includes(level)
          ? currentValues.filter((l) => l !== level)
          : [...currentValues, level],
      };
    });
  }, []);

  const formatChecked = useCallback((checkedArray, valuesArray) => {
    if (!checkedArray || checkedArray.length === 0) return "";
    return checkedArray
      .map(
        (value) => valuesArray.find((option) => option.value === value)?.label
      )
      .filter(Boolean)
      .join(", ");
  }, []);

  const adjustTextareaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 7.5 * 16) + "px";
    }
  };

  return (
    <div className="text-gray-200 rounded-lg">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 text-xs uppercase">
              <th className="py-3 px-4">Version</th>
              <th className="py-3 px-4">Committed By</th>
              <th className="py-3 px-4">Timestamp</th>
              {promptType === "topic" && (
                <th className="py-3 px-4">Visibility</th>
              )}
              <th className="py-3 px-4">Supported Modalities</th>
              <th className="py-3 px-4">Comment</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(notes.release_notes || {})
              .sort((a, b) => Number.parseInt(b) - Number.parseInt(a))
              .map((version) => (
                <tr
                  key={version}
                  className="border-t border-gray-800 hover:bg-gray-800 transition-colors text-xs"
                >
                  <td className="py-4 px-4 pl-6">{version}</td>
                  <td className="py-4 px-4 text-gray-400">
                    {notes.release_notes[version].by}
                  </td>
                  <td className="py-4 px-4 text-gray-400">
                    {new Date(notes.release_notes[version].at).toLocaleString()}
                  </td>
                  {promptType === "topic" && (
                    <td className="py-4 px-4 w-[180px]">
                      {editingVersion === version ? (
                        visibilityLevels.map((option) => (
                          <label
                            key={option.value}
                            className="block mb-1 flex items-center"
                          >
                            <span className="block ml-2">{option.label}</span>
                            <span className="relative top-0 left-0 mt-0.5 ml-0.5 block w-5 h-5">
                              <input
                                type="checkbox"
                                name="visibility"
                                value={option.value}
                                checked={editedNote.visibility?.includes(
                                  option.value
                                )}
                                onChange={() =>
                                  handleCheckboxChange(
                                    "visibility",
                                    option.value,
                                    editedNote.visibility || []
                                  )
                                }
                                className="bg-gray-700 text-white rounded p-1 w-full"
                              />
                            </span>
                          </label>
                        ))
                      ) : (
                        <span className="flex items-center px-2 py-1 rounded text-xs">
                          {formatChecked(
                            notes.release_notes[version].visibility,
                            visibilityLevels
                          )}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="py-1 px-4">
                    {editingVersion === version ? (
                      supportedModalityLevels.map((option) => (
                        <label
                          key={option.value}
                          className="block mb-1 flex items-center"
                        >
                          <span className="block ml-2">{option.label}</span>
                          <span className="relative top-0 left-0 mt-0.5 ml-0.5 block w-5 h-5">
                            <input
                              type="checkbox"
                              name="supported_modality"
                              value={option.value}
                              checked={editedNote.supported_modalities?.includes(
                                option.value
                              )}
                              onChange={() =>
                                handleCheckboxChange(
                                  "supported_modalities",
                                  option.value,
                                  editedNote.supported_modalities || [
                                    "voice",
                                    "text",
                                  ]
                                )
                              }
                              className="bg-gray-700 text-white rounded p-1 w-full"
                            />
                          </span>
                        </label>
                      ))
                    ) : (
                      <span className="flex items-center px-2 py-1 rounded text-xs">
                        {formatChecked(
                          notes.release_notes[version].supported_modalities,
                          supportedModalityLevels
                        )}
                      </span>
                    )}
                  </td>
                  <td className="py-1 px-4">
                    <div className="flex items-center h-full">
                      <textarea
                        ref={(el) => (textareaRefs.current[version] = el)}
                        name="comment"
                        value={
                          editingVersion === version
                            ? editedNote?.release_notes?.[version]?.comment
                            : notes?.release_notes?.[version]?.comment || ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          adjustTextareaHeight(e.target);
                        }}
                        className={`text-white p-2 w-full resize-none ${
                          editingVersion === version
                            ? "bg-gray-700"
                            : "bg-transparent disabled"
                        }`}
                        rows="1"
                        style={{
                          width: "400px",
                          minHeight: "1.5em",
                          maxHeight: "7.5em",
                        }}
                        disabled={editingVersion !== version}
                      />
                    </div>
                  </td>
                  <td className="py-1 px-4 w-[70px]">
                    {editingVersion === version ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSave(version)}
                          className="text-blue-500 hover:text-blue-400 transition-colors"
                        >
                          <FaSave />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-gray-500 hover:text-gray-400 transition-colors"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(version)}
                        className="text-blue-500 hover:text-blue-400 transition-colors"
                      >
                        <FaEdit />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReleaseNoteTab;
